import { useCallback, useEffect, useState } from "react";
import { adminFailedReports, adminRetryFailedReport } from "../../lib/admin";
import { Loader2, RefreshCw, AlertTriangle, MessageCircle, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

const FAILURE_LABELS = {
    NEEDS_BETTER_INPUT: "Needs better input",
    STYLING_FAILED: "Report writing failed",
    GENERATION_FAILED: "Image generation failed",
    REPORT_QA_FAILED: "Final QA failed",
};

function StagePill({ stage, retryable }) {
    const isFailure = Boolean(FAILURE_LABELS[stage]);
    const cls = isFailure
        ? "bg-rose-100 text-rose-800"
        : retryable
        ? "bg-charcoal/5 text-charcoal/70"
        : "bg-amber-100 text-amber-800";
    return (
        <span className={`inline-flex items-center rounded-full ${cls} px-2.5 py-0.5 text-[10px] uppercase tracking-[0.16em]`} data-testid="failed-report-stage-pill">
            {FAILURE_LABELS[stage] || stage || "—"}
        </span>
    );
}

export default function AdminFailedReports() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [retrying, setRetrying] = useState({}); // id -> bool

    const load = useCallback((silent = false) => {
        if (!silent) setLoading(true);
        return adminFailedReports()
            .then((d) => setItems(d.items || []))
            .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load reports."))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(() => load(true), 20000);
        return () => clearInterval(t);
    }, [load]);

    async function retry(id) {
        setRetrying((r) => ({ ...r, [id]: true }));
        try {
            await adminRetryFailedReport(id);
            toast.success("Retry started — regenerating from the same photos + answers. WhatsApp/email will fire automatically once ready.");
            await load(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Retry failed to start.");
        } finally {
            setRetrying((r) => ({ ...r, [id]: false }));
        }
    }

    if (loading) {
        return (
            <div className="flex h-60 items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-gold" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="admin-failed-reports">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-serif text-3xl text-charcoal">Failed / stuck paid reports</h1>
                    <p className="text-[13.5px] text-ink">
                        Every paid couple whose report isn't ready yet — most commonly an LLM budget cap mid-pipeline.
                        Retry re-runs generation with the same stored photos + answers, no re-payment needed.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => load()}
                    data-testid="failed-reports-refresh"
                    className="inline-flex items-center gap-2 rounded-full border border-charcoal/15 px-4 py-2 text-[12.5px] text-charcoal hover:border-charcoal"
                >
                    <RefreshCw className="h-3.5 w-3.5" /> Refresh
                </button>
            </div>

            {items.length === 0 ? (
                <div className="rounded-[24px] bg-white p-10 text-center text-[14px] text-ink shadow-soft ring-1 ring-charcoal/5" data-testid="failed-reports-empty">
                    <AlertTriangle className="mx-auto mb-3 h-6 w-6 text-emerald-600" />
                    Nothing stuck — every paid report is ready or still actively generating.
                </div>
            ) : (
                <div className="space-y-4">
                    {items.map((it) => (
                        <div
                            key={it.id}
                            className="rounded-[20px] bg-white p-5 shadow-soft ring-1 ring-charcoal/5"
                            data-testid={`failed-report-row-${it.id}`}
                        >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                    <div className="font-serif text-lg text-charcoal">
                                        {it.bride_name || "Bride"} &amp; {it.groom_name || "Groom"}
                                    </div>
                                    <div className="mt-1 flex flex-wrap items-center gap-3 text-[12px] text-charcoal/60">
                                        {it.phone && (
                                            <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{it.phone}</span>
                                        )}
                                        {it.email && (
                                            <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{it.email}</span>
                                        )}
                                        <span>paid ₹{((it.amount_paise || 0) / 100).toLocaleString("en-IN")}</span>
                                        <span>{it.paid_at ? new Date(it.paid_at).toLocaleString("en-IN") : ""}</span>
                                    </div>
                                </div>
                                <StagePill stage={it.pipeline_status} retryable={it.is_retryable} />
                            </div>

                            {(it.pipeline_failure_reason || it.unlock_error) && (
                                <p className="mt-3 rounded-[12px] bg-rose-50 p-3 text-[12.5px] text-rose-800" data-testid={`failed-report-reason-${it.id}`}>
                                    {it.pipeline_failure_reason || it.unlock_error}
                                </p>
                            )}

                            {Array.isArray(it.qa_log) && it.qa_log.length > 0 && (
                                <div className="mt-3 space-y-1">
                                    {it.qa_log.map((l, i) => (
                                        <div key={i} className="text-[11.5px] text-charcoal/50">
                                            <span className={l.ok ? "text-emerald-600" : "text-rose-600"}>{l.ok ? "✓" : "✗"}</span>{" "}
                                            {l.stage}{l.detail ? ` — ${l.detail}` : ""}
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="mt-4 flex items-center gap-3">
                                <button
                                    type="button"
                                    disabled={!it.is_retryable || retrying[it.id]}
                                    onClick={() => retry(it.id)}
                                    data-testid={`failed-report-retry-${it.id}`}
                                    className="btn-gold !py-2 !px-5 text-[13px] disabled:cursor-not-allowed disabled:opacity-50"
                                    title={!it.is_retryable ? "Already generating — please wait" : "Retry generation"}
                                >
                                    {retrying[it.id] ? (
                                        <span className="inline-flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Retrying…</span>
                                    ) : (
                                        "Retry generation"
                                    )}
                                </button>
                                {!it.is_retryable && (
                                    <span className="inline-flex items-center gap-1.5 text-[12px] text-amber-700">
                                        <MessageCircle className="h-3.5 w-3.5" /> currently generating — will auto-ping customer on WhatsApp/email once ready
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
